import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Grid,
  Autocomplete,
  TextField,
  Pagination,
  Chip,
  Link,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import SidebarLayout from '../components/SidebarLayout';

interface QQRecord {
  id: number;
  client_id: string;
  status: number;
  execution_time: string;
  content: string;
  song_url?: string;
  user?: { username: string };
}

interface Statistics {
  total: number;
  success_count: number;
  failed_count: number;
  today_count: number;
}

interface UserOption {
  id: number;
  username: string;
}

const QQMusicDashboard = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState<QQRecord[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | ''>('');
  const [userSearch, setUserSearch] = useState('');
  const [statistics, setStatistics] = useState<Statistics>({
    total: 0,
    success_count: 0,
    failed_count: 0,
    today_count: 0,
  });
  const [status, setStatus] = useState('');
  const [clientId, setClientId] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params: Record<string, string | number | undefined> = {
        userId: selectedUser || undefined,
        clientId: clientId.trim() || undefined,
        status: status || undefined,
        page,
        limit,
        startTime: startTime || undefined,
        endTime: endTime || undefined,
      };
      const [recResp, statsResp] = await Promise.all([
        axios.get('/api/qqmusic/records', { params }),
        axios.get('/api/qqmusic/statistics', { params: { userId: selectedUser || undefined } }),
      ]);
      setRecords(recResp.data.records || []);
      setTotal(recResp.data.total || 0);
      setStatistics(statsResp.data);
    } catch (err) {
      console.error('获取QQ音乐数据失败:', err);
    }
  }, [selectedUser, clientId, status, page, limit, startTime, endTime]);

  const fetchUsers = async (search = '') => {
    try {
      const resp = await axios.get('/api/users', { params: { search, limit: 50 } });
      setUsers(resp.data.users || []);
    } catch (e) {
      console.error('获取用户列表失败:', e);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (user?.role === 'admin') fetchUsers();
  }, [user]);

  const successRate = statistics.total > 0
    ? Math.round((statistics.success_count / statistics.total) * 100)
    : 0;

  return (
    <SidebarLayout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4">QQ音乐评论统计</Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">总发送数</Typography>
                <Typography variant="h4">{statistics.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">成功次数</Typography>
                <Typography variant="h4" color="success.main">{statistics.success_count}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">成功率</Typography>
                <Typography variant="h4" color={successRate >= 80 ? 'success.main' : 'warning.main'}>
                  {successRate}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom variant="body2">今日发送</Typography>
                <Typography variant="h4" color="primary">{statistics.today_count}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {user?.role === 'admin' && (
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={users}
                getOptionLabel={(o) => o.username}
                value={users.find((u) => u.id === selectedUser) || null}
                onChange={(_, v) => {
                  setSelectedUser(v ? v.id : '');
                  setPage(1);
                }}
                inputValue={userSearch}
                onInputChange={(_, v) => {
                  setUserSearch(v);
                  fetchUsers(v);
                }}
                renderInput={(params) => <TextField {...params} label="选择用户" size="small" fullWidth />}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                clearOnBlur={false}
              />
            </Grid>
          )}
          <Grid item xs={12} md={2}>
            <TextField
              label="客户端 ID"
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setPage(1);
              }}
              placeholder="如 001"
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>状态</InputLabel>
              <Select
                value={status}
                label="状态"
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="">全部</MenuItem>
                <MenuItem value="1">成功</MenuItem>
                <MenuItem value="0">失败</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="开始时间"
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="结束时间"
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>发送时间</TableCell>
                <TableCell>状态</TableCell>
                <TableCell>评论内容</TableCell>
                <TableCell>目标歌曲</TableCell>
                <TableCell>操作人</TableCell>
                <TableCell>客户端 ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((rec) => (
                <TableRow key={rec.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(rec.execution_time).toLocaleString('zh-CN')}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={rec.status === 1 ? '成功' : '失败'}
                      size="small"
                      color={rec.status === 1 ? 'success' : 'error'}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" noWrap title={rec.content}>
                      {rec.content}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    {rec.song_url ? (
                      <Link
                        href={rec.song_url}
                        target="_blank"
                        rel="noopener"
                        variant="body2"
                        sx={{
                          display: 'block',
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {rec.song_url.replace('https://y.qq.com', '')}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{rec.user?.username || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Chip label={rec.client_id || '-'} size="small" variant="outlined" />
                  </TableCell>
                </TableRow>
              ))}
              {records.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    暂无数据
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
          <Pagination
            count={Math.ceil(total / limit) || 1}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            总条数: {total}
          </Typography>
        </Box>
      </Container>
    </SidebarLayout>
  );
};

export default QQMusicDashboard;
